import { Controller } from "@hotwired/stimulus"
import { Upload } from "tus-js-client"

// Uploads files to the media library in resumable chunks. Each chunk stays
// under Cloudflare's 100MB request limit, and a dropped connection resumes
// where it stopped. A finished file is handed to the library, which
// processes it in the background.
export default class extends Controller {
  static targets = ["input", "list", "template"]
  static values = { endpoint: String, finishUrl: String, chunkSize: { type: Number, default: 50 * 1024 * 1024 }, maxBytes: Number }

  choose() {
    Array.from(this.inputTarget.files).forEach((file) => this.upload(file))
    this.inputTarget.value = ""
  }

  drop(event) {
    event.preventDefault()
    Array.from(event.dataTransfer.files).forEach((file) => this.upload(file))
  }

  dragover(event) {
    event.preventDefault()
  }

  upload(file) {
    const row = this.addRow(file.name)
    if (this.maxBytesValue && file.size > this.maxBytesValue) {
      this.fail(row, "This file is larger than the library accepts.")
      return
    }

    const upload = new Upload(file, {
      endpoint: this.endpointValue,
      chunkSize: this.chunkSizeValue,
      retryDelays: [0, 2000, 5000, 10000, 20000],
      metadata: { filename: file.name, filetype: file.type },
      headers: { "X-CSRF-Token": this.csrfToken },
      removeFingerprintOnSuccess: true,
      onProgress: (sent, total) => this.progress(row, sent / total),
      // A full library answers 413 with the reason, so show that instead.
      onError: (error) => this.fail(row, error.originalResponse?.getStatus() === 413
        ? error.originalResponse.getBody()
        : "The upload stopped. Choose the file again to resume."),
      onSuccess: () => this.finish(row, upload.url, file.name)
    })

    upload.findPreviousUploads().then((previous) => {
      if (previous.length) upload.resumeFromPreviousUpload(previous[0])
      upload.start()
    })
  }

  async finish(row, uploadUrl, filename) {
    this.status(row, "Processing…")
    const response = await fetch(this.finishUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken },
      body: JSON.stringify({ upload_url: uploadUrl, title: filename.replace(/\.[^.]+$/, "") })
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok) return this.fail(row, body.error || "The file could not be added to the library.")

    const link = document.createElement("a")
    link.href = body.url
    link.textContent = "Open"
    link.className = "underline"
    this.status(row, "Added. ")
    row.querySelector("[data-role=status]").appendChild(link)
    this.progress(row, 1)
  }

  addRow(name) {
    const row = this.templateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector("[data-role=name]").textContent = name
    this.listTarget.prepend(row)
    return row
  }

  progress(row, fraction) {
    row.querySelector("[data-role=bar]").style.width = `${Math.round(fraction * 100)}%`
    if (fraction < 1) this.status(row, `${Math.round(fraction * 100)}%`)
  }

  status(row, text) {
    row.querySelector("[data-role=status]").textContent = text
  }

  fail(row, message) {
    this.status(row, message)
    row.querySelector("[data-role=bar]").classList.replace("bg-primary-600", "bg-danger-600")
  }

  get csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content
  }
}
